import type { Attachment, Communication, CommunicationPayload, Patient } from '@medplum/fhirtypes'
import { createReference, type MedplumClient } from '@medplum/core'

const PAYLOAD_TYPE_URL = 'http://behuman.com/fhir/StructureDefinition/payload-type'

export const CATEGORY_SYSTEM = 'http://behuman.com/fhir/communication-category'
export const CATEGORY_CODE = 'care-coordination-note'

export function uploadFiles(
  medplum: MedplumClient,
  patient: Patient,
  files: File[],
): Promise<Attachment[]> {
  return Promise.all(
    files.map(async (file) => {
      const binary = await medplum.createBinary({
        data: file,
        contentType: file.type,
        securityContext: createReference(patient),
      })
      return {
        contentType: file.type,
        url: binary.url ?? `${medplum.getBaseUrl()}Binary/${binary.id}`,
        title: file.name,
        size: file.size,
      } satisfies Attachment
    }),
  )
}

export function extractNote(comm: Communication): string {
  return (
    comm.payload?.find((p) =>
      p.extension?.some((e) => e.url === PAYLOAD_TYPE_URL && e.valueCode === 'note'),
    )?.contentString ?? ''
  )
}

export function extractNextAction(comm: Communication): string {
  return (
    comm.payload?.find((p) =>
      p.extension?.some((e) => e.url === PAYLOAD_TYPE_URL && e.valueCode === 'next-action'),
    )?.contentString ?? ''
  )
}

export function extractAttachments(comm: Communication): Attachment[] {
  return (
    comm.payload
      ?.filter((p) =>
        p.extension?.some((e) => e.url === PAYLOAD_TYPE_URL && e.valueCode === 'attachment'),
      )
      .map((p) => p.contentAttachment)
      .filter((a): a is Attachment => a != null) ?? []
  )
}

export function buildCommunicationPayload(
  note: string,
  nextAction: string,
  attachments: Attachment[] = [],
): CommunicationPayload[] {
  return [
    {
      contentString: note,
      extension: [{ url: PAYLOAD_TYPE_URL, valueCode: 'note' }],
    },
    ...(nextAction
      ? [
          {
            contentString: nextAction,
            extension: [{ url: PAYLOAD_TYPE_URL, valueCode: 'next-action' }],
          },
        ]
      : []),
    ...attachments.map((attachment) => ({
      contentAttachment: attachment,
      extension: [{ url: PAYLOAD_TYPE_URL, valueCode: 'attachment' }],
    })),
  ]
}
